import React from 'react';
import moment from 'moment';
import { Column } from 'react-foundation';
import { Dial } from '../../components/Dial';
import { Icon } from '../../components/Icon';
import { Meter } from '../../components/Meter';
import styled from 'styled-components';
import legendIconConsumptionInQuota from '../../static/images/dial_legend_consumption_in_package.svg';
import legendIconConsumptionOverQuota from '../../static/images/dial_legend_consumption_exceeded.svg';
import legendIconConsumptionEstimate from '../../static/images/dial_legend_comsumption_estimate.svg';

interface Props {
	takeFullWidth: boolean;
	simpleDials: boolean;
	usage: any;
	dataPerSim: number;
	moisPerSim: number;
	dataTotal: number;
	simsCount: number;
	moisTotal: number;
	billingPeriod: any;
	// TODO more specific parameter types
	getWarningValue: (dataPerSim: any, moisPerSim: any, simCount: any, type: any) => number;
	getMessageAndVoiceUsage: (usage: any) => number;
	getEstimate: (usedAmount: any, daysOfConsumption: any, periodLength: any) => number;
	getMaxValue: (packageSize: any, type: any) => number;
	getDataUsage: (usage: any, isLatestBillingPeriod: boolean) => number;
}

const Centralized = styled.div<{ alignLeft?: boolean }>`
	margin: auto;
	text-align: ${(props) => (props.alignLeft ? 'left' : 'center')};
`;

const RoundedBox = styled.div`
	margin: auto;
	margin: 1.8em auto 0.8em auto;
	padding: 2.5rem;
	max-width: 22rem;
	width: 100%;
	border: 1px solid #eaeaea;
	border-radius: 10%;
	box-shadow: 0px 8px 50px -18px #969696;
`;

const Info = styled.div`
	text-align: left;
	margin: auto;
	max-width: 20rem;

	.consumption-legend {
		width: 0.8em;
		height: 0.8em;
		float: left;
		margin-top: 0.3rem;
		margin-right: 0.3rem;
	}

	.legend-consumed {
		border-color: #94c92d;
	}

	.icon-consumption-estimate {
		background: url(${legendIconConsumptionEstimate}) 0 0 no-repeat;
	}
	.icon-consumption-over-quota {
		background: url(${legendIconConsumptionOverQuota}) 0 0 no-repeat;
	}
	.icon-consumption-in-quota {
		background: url(${legendIconConsumptionInQuota}) 0 0 no-repeat;
	}
`;

const getConsumptionOverPackage = (packageSize: number, consumption: number) => {
	if (packageSize > consumption) {
		return 0;
	}
	return (consumption - packageSize).toFixed(2);
};

const getIncludedData = (dataTotal: any, dataLimit: any) => {
	if (dataTotal === Infinity) {
		return 'Rajaton määrä dataa';
	} else {
		return `${dataLimit ? dataLimit : '0'}Gt`;
	}
};

export const Consumption: React.FC<Props> = (props: Props) => {
	const isOngoingBillingPeriod = moment().isBetween(props.billingPeriod.startDate, props.billingPeriod.endDate, 'day', '[]');
	const billingPeriodLength = moment(props.billingPeriod.endDate).diff(props.billingPeriod.startDate, 'days') + 1;
	const daysIntoBillingPeriod = isOngoingBillingPeriod ? moment().diff(props.billingPeriod.startDate, 'days') + 1 : billingPeriodLength;
	return (
		<div>
			{!props.simpleDials && (
				<div>
					<Column large={props.takeFullWidth ? 12 : 6} small={12}>
						{/* TODO: calculate reasonable values for warningValue and maxValue.*/}
						<Dial
							consumption={Number(props.getDataUsage(props.usage.toJS().analysedUsageSummary, isOngoingBillingPeriod))}
							estimate={props.getEstimate(props.getDataUsage(props.usage.toJS().analysedUsageSummary, isOngoingBillingPeriod), daysIntoBillingPeriod, billingPeriodLength)}
							packageSize={props.dataTotal}
							warningValue={props.getWarningValue(props.dataPerSim, props.moisPerSim, props.simsCount, 'data')}
							maxValue={props.getMaxValue(props.dataTotal, 'data')}
							type="data"
							isOngoingBillingPeriod={isOngoingBillingPeriod}
						/>
					</Column>
					<Column large={props.takeFullWidth ? 12 : 6} small={12}>
						<Dial
							consumption={Number(props.getMessageAndVoiceUsage(props.usage.toJS().analysedUsageSummary))}
							estimate={props.getEstimate(props.getMessageAndVoiceUsage(props.usage.toJS().analysedUsageSummary), daysIntoBillingPeriod, billingPeriodLength)}
							packageSize={props.moisTotal}
							warningValue={props.getWarningValue(props.dataPerSim, props.moisPerSim, props.simsCount, 'voice')}
							maxValue={props.getMaxValue(props.moisTotal, 'voice')}
							type="voice"
							isOngoingBillingPeriod={isOngoingBillingPeriod}
						/>
					</Column>
				</div>
			)}
			{props.simpleDials && (
				<div>
					<Column large={props.takeFullWidth ? 12 : 6} small={12}>
						<RoundedBox>
							<Centralized>
								<Icon name="medium--data" />
							</Centralized>
							{isOngoingBillingPeriod && (
								<Meter quantity={Number(parseFloat(props.usage.toJS().analysedUsageSummary.sumDataUsageExcludingUnlimited).toFixed(2))} unit="Gt" description="Datan kulutus" />
							)}
							{!isOngoingBillingPeriod && (
								<Meter quantity={Number(parseFloat(props.usage.toJS().analysedUsageSummary.sumOverAllDataUsage).toFixed(2))} unit="Gt" description="Datan kokonaiskulutus kaudella" />
							)}
						</RoundedBox>
						{isOngoingBillingPeriod && (
							<Info>
								<div className="consumption-legend icon-consumption-in-quota" />
								<div>{getIncludedData(props.dataTotal, props.usage.toJS().analysedUsageSummary.dataLimit)} sisältyy kuukausihintaan</div>
								{props.dataTotal !== Infinity && (
									<div>
										<div className="consumption-legend icon-consumption-over-quota" />
										<div>
											{getConsumptionOverPackage(props.usage.toJS().analysedUsageSummary.dataLimit, Number(props.usage.toJS().analysedUsageSummary.sumDataUsageExcludingUnlimited))}Gt kuukausihintaan
											sisältymätön käyttö
										</div>
									</div>
								)}
								{props.usage.toJS().analysedUsageSummary.sumDataEstimateExcludingUnlimited !== null && (
									<div>
										<div className="consumption-legend icon-consumption-estimate" />
										<div>Käyttöennuste {parseFloat(props.usage.toJS().analysedUsageSummary.sumDataEstimateExcludingUnlimited).toFixed(2)} Gt</div>
									</div>
								)}
							</Info>
						)}
					</Column>
					<Column large={props.takeFullWidth ? 12 : 6} small={12}>
						<RoundedBox>
							<Centralized>
								<Icon name="medium--chat" />
							</Centralized>
							<Meter quantity={Number(props.usage.toJS().analysedUsageSummary.sumOverAllMoisUsageEur)} unit="€" description="puhelujen ja tekstarien maksut" />
						</RoundedBox>
					</Column>
				</div>
			)}
		</div>
	);
};
